import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  analyzeGoldenLoan,
  runGoldenSuite,
  saveGoldenHumanLabel,
} from "./api";
import {
  createEmptyHumanRow,
  getComponentRows,
  HUMAN_STATUSES,
  INCOME_TYPES,
  normalizeHumanLabel,
  serializeHumanLabel,
} from "./goldenEval";

export default function HumanLabelEditor({
  env,
  goldenLoan,
  suiteLoan,
  suiteRunId,
  onSaved,
  onSuiteQueued,
}) {
  const suiteSourcePathKey = getComponentRows(suiteLoan)
    .map((row) => row.path)
    .sort()
    .join("|");
  const goldenHumanLabelKey = JSON.stringify(
    goldenLoan?.humanLabel?.components || [],
  );
  const [label, setLabel] = useState(() =>
    buildEditorLabel(goldenLoan, suiteLoan),
  );
  const [submittingAction, setSubmittingAction] = useState("");

  useEffect(() => {
    setLabel(buildEditorLabel(goldenLoan, suiteLoan));
    // Source amounts can refresh while the reviewer is editing. Paths identify
    // the stable editor shape without resetting in-progress input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    goldenLoan?.id,
    goldenHumanLabelKey,
    suiteRunId,
    suiteSourcePathKey,
  ]);

  if (!goldenLoan) {
    return null;
  }
  const hasValidComponent =
    serializeHumanLabel(label).components.length > 0;

  const updateRow = (id, key, value) => {
    setLabel((current) => ({
      ...current,
      rows: current.rows.map((row) =>
        row.id === id ? { ...row, [key]: value } : row,
      ),
    }));
  };

  const removeRow = (id) => {
    setLabel((current) => ({
      ...current,
      rows: current.rows.filter((row) => row.id !== id),
    }));
  };

  const save = async (action) => {
    setSubmittingAction(action);
    try {
      const humanLabel = serializeHumanLabel(label);
      const result = await saveGoldenHumanLabel(
        goldenLoan.id,
        humanLabel,
        env,
      );
      if (!result.success) {
        throw new Error(result.reason || "Could not save the human label.");
      }
      onSaved(humanLabel);

      if (action === "analyze") {
        const analysis = await analyzeGoldenLoan(
          goldenLoan.id,
          suiteRunId,
          env,
        );
        if (!analysis.success) {
          throw new Error(analysis.reason || "Could not analyze this label.");
        }
        toast.success("Human label saved and analysis refreshed.");
      } else if (action === "rerun") {
        const suite = await runGoldenSuite(
          { goldenLoanIds: [goldenLoan.id] },
          env,
        );
        if (!suite.success) {
          throw new Error(suite.reason || "Could not queue this loan.");
        }
        onSuiteQueued(suite);
        toast.success("Human label saved and a fresh evaluation was queued.");
      } else {
        toast.success("Optional human label saved.");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingAction("");
    }
  };

  return (
    <section className="icr-human-editor" aria-labelledby="human-label-title">
      <div className="icr-eval-section__header">
        <div>
          <span className="icr-eval-eyebrow">Optional baseline</span>
          <h3 id="human-label-title">Human truth label</h3>
          <p>
            Add only reviewed values. Leave this unset when there is no human
            truth; Addy and Harness can still be compared.
          </p>
        </div>
        <span className="icr-truth-state">
          {label.rows.length > 0 ? `${label.rows.length} row(s)` : "No human truth"}
        </span>
      </div>

      <div className="icr-human-rows">
        {label.rows.map((row, index) => (
          <div className="icr-human-row" key={row.id}>
            <label>
              Borrower
              <input
                value={row.borrowerName}
                onChange={(event) =>
                  updateRow(row.id, "borrowerName", event.target.value)
                }
                placeholder={`Borrower ${index + 1}`}
              />
            </label>
            <label>
              Employment or business
              <input
                value={row.employmentName || row.businessName}
                onChange={(event) =>
                  updateRow(row.id, "employmentName", event.target.value)
                }
                placeholder="Employer or business"
              />
            </label>
            <label>
              Income type
              <select
                value={row.incomeType}
                onChange={(event) =>
                  updateRow(row.id, "incomeType", event.target.value)
                }
              >
                {INCOME_TYPES.map((type) => (
                  <option key={type}>{type}</option>
                ))}
              </select>
            </label>
            <label>
              Monthly amount
              <input
                type="number"
                min="0"
                step="0.01"
                value={row.amount}
                onChange={(event) =>
                  updateRow(row.id, "amount", event.target.value)
                }
                placeholder="0.00"
              />
            </label>
            <label>
              Status
              <select
                value={row.status}
                onChange={(event) =>
                  updateRow(row.id, "status", event.target.value)
                }
              >
                {HUMAN_STATUSES.map((status) => (
                  <option key={status}>{status}</option>
                ))}
              </select>
            </label>
            <label className="icr-human-row__notes">
              Notes
              <input
                value={row.notes}
                onChange={(event) =>
                  updateRow(row.id, "notes", event.target.value)
                }
                placeholder="Optional reviewer context"
              />
            </label>
            <button
              className="icr-btn icr-btn--danger icr-human-row__remove"
              type="button"
              onClick={() => removeRow(row.id)}
              aria-label={`Remove human label row ${index + 1}`}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="icr-human-editor__footer">
        <button
          className="icr-btn"
          type="button"
          onClick={() =>
            setLabel((current) => ({
              ...current,
              rows: [...current.rows, createEmptyHumanRow()],
            }))
          }
        >
          Add label row
        </button>
        <div className="icr-golden-actions">
          <button
            className="icr-btn"
            type="button"
            disabled={
              Boolean(submittingAction) || !hasValidComponent || env !== "dev"
            }
            onClick={() => save("save")}
          >
            {submittingAction === "save" ? "Saving…" : "Save"}
          </button>
          <button
            className="icr-btn"
            type="button"
            disabled={
              Boolean(submittingAction) ||
              !hasValidComponent ||
              !suiteRunId ||
              env !== "dev"
            }
            title={
              suiteRunId
                ? "Save and re-run the judge for this suite result"
                : "Select a suite run before analyzing"
            }
            onClick={() => save("analyze")}
          >
            {submittingAction === "analyze" ? "Analyzing…" : "Save and analyze"}
          </button>
          <button
            className="icr-btn icr-btn--primary"
            type="button"
            disabled={
              Boolean(submittingAction) || !hasValidComponent || env !== "dev"
            }
            onClick={() => save("rerun")}
          >
            {submittingAction === "rerun" ? "Queuing…" : "Save and rerun"}
          </button>
        </div>
      </div>
    </section>
  );
}

function buildEditorLabel(goldenLoan, suiteLoan) {
  if (goldenLoan?.humanLabel?.components?.length) {
    return normalizeHumanLabel(goldenLoan.humanLabel);
  }

  const sourceRows = getComponentRows(suiteLoan);
  if (sourceRows.length === 0) {
    return normalizeHumanLabel(null);
  }

  return {
    notes: "",
    rows: sourceRows.map((row) =>
      createEmptyHumanRow({
        path: row.path,
        borrowerName: row.borrower,
        employmentName: row.employment,
        incomeType: row.incomeType,
      }),
    ),
  };
}
